import { Component, Inject, OnInit } from '@angular/core';
import { formatDate } from '@angular/common';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { consts } from '../../../consts';
import { Utils } from '../../../shared/utils';
import { InvestmentPropertyDepositsModel } from './investment-property.model';
import { InvestmentPropertyService } from './investment-property.service';
import { EntityFormOption } from '../../../modules_v2/finance/entity-form/entity-form.utils';
import {
  RealStateInvestmentFormLookupsFacade,
  RealStatePayLookupKind,
} from '../../../modules_v2/investment/real-state-investment/services/real-state-investment-form-lookups.facade';
import { NotificationService } from '../../../modules_v2/shared/services/notification.service';
import { ConfirmService } from '../../../modules_v2/shared/services/confirm.service';
import { ConfirmDialogDetail } from '../../../modules_v2/shared/components/confirm-dialog.component';

export interface InvestmentPropertyPayInvestment {
  uid: string;
  total: number;
  investmentDescription?: string;
  contactsName?: string;
}

export interface InvestmentPropertyDialogPayData {
  investment: InvestmentPropertyPayInvestment;
  editUid?: string;
}

export const PROPERTY_PAY_METHODS = {
  cash: consts.TYPEPAY_CASH,
  creditCard: consts.TYPEPAY_CREDITCARD_PAY,
  transfer: consts.TYPEPAY_BANKTRANSFERENCE,
  debit: consts.TYPEPAY_DEBITENTRY,
  partner: consts.TYPE_SOCIO,
  creditPolicy: consts.TYPE_POLIZACREDITO,
};

const PAY_METHOD_OPTIONS: string[] = [
  PROPERTY_PAY_METHODS.cash,
  PROPERTY_PAY_METHODS.creditCard,
  PROPERTY_PAY_METHODS.transfer,
  PROPERTY_PAY_METHODS.debit,
  PROPERTY_PAY_METHODS.partner,
  PROPERTY_PAY_METHODS.creditPolicy,
];

const KIND_CONTROLS: Record<RealStatePayLookupKind, string> = {
  bankAccounts: 'bankAccount',
  paymentCards: 'paymentCard',
  cashRegisters: 'cashRegister',
  partners: 'partner',
  creditPolicies: 'creditPolicy',
};

@Component({
  selector: 'convfact-investmentpropertypay',
  templateUrl: './investment-property-dialog-pay.component.html',
  styleUrls: ['./investment-property-dialog-pay.component.scss'],
  standalone: false
})
export class InvestmentPropertyDialogPayComponent implements OnInit {
  readonly methods = PROPERTY_PAY_METHODS;
  readonly methodOptions = PAY_METHOD_OPTIONS;

  readonly methodConfig = {
    label: 'Tipo de pago',
    placeholder: 'Seleccionar tipo de pago…',
    required: true,
    strict: true,
    options: PAY_METHOD_OPTIONS.map(method => ({ id: method, display: method })),
    onChange: () => this.onTypePayChange(),
  };

  form: UntypedFormGroup;

  ready = false;
  loadError = false;
  saving = false;
  savingMessage = '';
  submitted = false;
  saveError = '';

  deposits: InvestmentPropertyDepositsModel[] = [];
  editing: InvestmentPropertyDepositsModel | null = null;

  options: Record<RealStatePayLookupKind, EntityFormOption[]> = {
    bankAccounts: [],
    paymentCards: [],
    cashRegisters: [],
    partners: [],
    creditPolicies: [],
  };
  loadingOptions: Partial<Record<RealStatePayLookupKind, boolean>> = {};

  configs: Record<RealStatePayLookupKind, any> = {
    bankAccounts: { label: 'Cuenta bancaria', placeholder: 'Seleccionar cuenta…', required: true, autoSelectSingle: true, strict: true, options: [] },
    paymentCards: { label: 'Tarjeta de crédito', placeholder: 'Seleccionar tarjeta…', required: true, autoSelectSingle: true, strict: true, options: [] },
    cashRegisters: { label: 'Caja / Efectivo', placeholder: 'Seleccionar caja…', required: true, autoSelectSingle: true, strict: true, options: [] },
    partners: { label: 'Socio', placeholder: 'Seleccionar socio…', required: true, autoSelectSingle: true, strict: true, options: [] },
    creditPolicies: { label: 'Póliza de crédito', placeholder: 'Seleccionar póliza…', required: true, autoSelectSingle: true, strict: true, options: [] },
  };

  private readonly emptyHints: Record<RealStatePayLookupKind, string> = {
    bankAccounts: 'No tienes cuentas bancarias dadas de alta.',
    paymentCards: 'No tienes tarjetas de crédito dadas de alta.',
    cashRegisters: 'No tienes cajas dadas de alta.',
    partners: 'No tienes socios dados de alta.',
    creditPolicies: 'No tienes pólizas de crédito dadas de alta.',
  };

  private readonly missingLabels: Record<RealStatePayLookupKind, string> = {
    bankAccounts: 'Cuenta bancaria',
    paymentCards: 'Tarjeta de crédito',
    cashRegisters: 'Caja',
    partners: 'Socio',
    creditPolicies: 'Póliza de crédito',
  };

  private changed = false;
  private requestedEditDone = false;
  private readonly optionRequests = new Map<RealStatePayLookupKind, Promise<void>>();
  private readonly pendingSelection = new Map<RealStatePayLookupKind, { id: any; label: string }>();
  private readonly loadedKinds = new Set<RealStatePayLookupKind>();

  static open(dialog: MatDialog, data: InvestmentPropertyDialogPayData): Observable<boolean> {
    return dialog.open<InvestmentPropertyDialogPayComponent, InvestmentPropertyDialogPayData, boolean>(InvestmentPropertyDialogPayComponent, {
      width: '680px',
      maxWidth: '96vw',
      maxHeight: '90vh',
      autoFocus: false,
      data,
    }).afterClosed().pipe(map(result => result === true));
  }

  constructor(
    private fb: UntypedFormBuilder,
    private dialogRef: MatDialogRef<InvestmentPropertyDialogPayComponent, boolean>,
    @Inject(MAT_DIALOG_DATA) public data: InvestmentPropertyDialogPayData,
    private investmentPropertyService: InvestmentPropertyService,
    private lookups: RealStateInvestmentFormLookupsFacade,
    private notify: NotificationService,
    private confirm: ConfirmService,
  ) {
    this.form = this.fb.group({
      typePay: [null],
      depositDate: [new Date()],
      amount: [null],
      commissions: [null],
      bankAccount: [null],
      paymentCard: [null],
      cashRegister: [null],
      partner: [null],
      creditPolicy: [null],
    });
  }

  get subtitle(): string {
    const investment = this.data && this.data.investment;
    if (!investment) return '';
    return [investment.investmentDescription, investment.contactsName].filter(Boolean).join(' · ');
  }

  get typePayControl(): UntypedFormControl {
    return this.form.get('typePay') as UntypedFormControl;
  }

  get depositDateControl(): UntypedFormControl {
    return this.form.get('depositDate') as UntypedFormControl;
  }

  get amountControl(): UntypedFormControl {
    return this.form.get('amount') as UntypedFormControl;
  }

  get commissionsControl(): UntypedFormControl {
    return this.form.get('commissions') as UntypedFormControl;
  }

  control(kind: RealStatePayLookupKind): UntypedFormControl {
    return this.form.get(KIND_CONTROLS[kind]) as UntypedFormControl;
  }

  selectedId(kind: RealStatePayLookupKind): any {
    const value = this.control(kind).value;
    return value && typeof value === 'object' && value.id !== undefined && value.id !== null && value.id !== '' ? value.id : null;
  }

  get typePay(): string | null {
    const value = this.typePayControl.value;
    return value && typeof value === 'object' && value.id ? value.id : null;
  }

  get total(): number {
    return this.round(Number(this.data.investment && this.data.investment.total) || 0);
  }

  get paidAmount(): number {
    return this.round(this.deposits.reduce((acc, d) => acc + (Number(d.amount) || 0), 0));
  }

  get remaining(): number {
    return this.round(this.total - this.paidAmount);
  }

  get isPaid(): boolean {
    return this.deposits.length > 0 && this.remaining <= 0;
  }

  get showForm(): boolean {
    return !this.isPaid || !!this.editing;
  }

  get maxAmount(): number {
    return this.round(this.remaining + (this.editing ? Number(this.editing.amount) || 0 : 0));
  }

  get needsBankAccount(): boolean {
    return this.typePay === this.methods.transfer || this.typePay === this.methods.debit;
  }

  get missingTypePay(): boolean { return !this.typePay; }
  get missingDate(): boolean { return !this.depositDateControl.value; }
  get missingBankAccount(): boolean { return this.needsBankAccount && !this.selectedId('bankAccounts'); }
  get missingPaymentCard(): boolean { return this.typePay === this.methods.creditCard && !this.selectedId('paymentCards'); }
  get missingCash(): boolean { return this.typePay === this.methods.cash && !this.selectedId('cashRegisters'); }
  get missingPartner(): boolean { return this.typePay === this.methods.partner && !this.selectedId('partners'); }
  get missingCreditPolicy(): boolean { return this.typePay === this.methods.creditPolicy && !this.selectedId('creditPolicies'); }

  get invalidCommissions(): boolean {
    const commissions = this.commissionsControl.value;
    return this.typePay === this.methods.transfer && this.hasValue(commissions) && (isNaN(Number(commissions)) || Number(commissions) < 0);
  }

  get invalidAmount(): boolean {
    const value = Number(this.amountControl.value);
    return !value || value <= 0 || value > this.maxAmount + 0.001;
  }

  get amountError(): string {
    const value = Number(this.amountControl.value);
    if (!value || value <= 0) return 'Indica un importe mayor que cero';
    return `No puede superar el pendiente (${Utils.FormatMoney(this.maxAmount)} €)`;
  }

  get hasErrors(): boolean {
    return this.missingTypePay || this.missingDate || this.missingBankAccount || this.missingPaymentCard
      || this.missingCash || this.missingPartner || this.missingCreditPolicy || this.invalidCommissions || this.invalidAmount;
  }

  ngOnInit(): void {
    this.loadDeposits(true);
  }

  retry(): void {
    this.loadDeposits(true);
  }

  onTypePayChange(): void {
    const kind = this.lookupKind(this.typePay);
    if (kind) this.ensureOptions(kind);
  }

  assignRemaining(): void {
    this.amountControl.setValue(this.maxAmount > 0 ? this.maxAmount : null);
  }

  startEdit(deposit: InvestmentPropertyDepositsModel): void {
    if (this.saving) return;
    this.editing = deposit;
    this.submitted = false;
    this.saveError = '';
    this.pendingSelection.clear();

    const type = this.methodOptions.indexOf(deposit.typePay) !== -1 ? deposit.typePay : null;
    this.form.patchValue({
      typePay: type ? { id: type, display: type } : null,
      depositDate: deposit.depositDate ? new Date(deposit.depositDate) : null,
      amount: Number(deposit.amount) || null,
      commissions: Number(deposit.commissions) || null,
      bankAccount: null,
      paymentCard: null,
      cashRegister: null,
      partner: null,
      creditPolicy: null,
    }, { emitEvent: false });

    const info = deposit.bankAccountInfo;
    this.rememberSelection('bankAccounts', this.guidOrNull(deposit.bankAccountsUid), info);
    this.rememberSelection('paymentCards', this.guidOrNull(deposit.paymentCardsUid), info);
    this.rememberSelection('cashRegisters', deposit.efectivoId || null, info);
    this.rememberSelection('partners', deposit.partnersId || null, info);
    this.rememberSelection('creditPolicies', deposit.polizaCreditoId || null, info);

    this.onTypePayChange();
    this.applyPendingSelections();
  }

  cancelEdit(): void {
    this.editing = null;
    this.resetForm();
  }

  save(): void {
    if (this.saving) return;
    this.submitted = true;
    this.saveError = '';
    if (this.hasErrors) return;

    const editing = this.editing;
    const payload = this.buildPayload();
    this.saving = true;
    this.savingMessage = editing ? 'Actualizando pago…' : 'Registrando pago…';
    this.dialogRef.disableClose = true;

    const request = editing
      ? this.investmentPropertyService.depositUpdate({ ...payload, uid: editing.uid })
      : this.investmentPropertyService.depositCreate(payload);

    request
      .then(ok => {
        if (ok !== true) throw new Error();
        this.changed = true;
        this.saving = false;
        this.dialogRef.disableClose = false;
        this.notify.ok(editing ? 'Pago actualizado correctamente' : 'Pago registrado correctamente');
        if (!editing || this.data.editUid) {
          this.dialogRef.close(true);
          return;
        }
        this.editing = null;
        this.resetForm();
        this.loadDeposits(false);
      })
      .catch(error => {
        this.saving = false;
        this.dialogRef.disableClose = false;
        this.saveError = (error && error.error && error.error.message)
          || (editing ? 'Ha ocurrido un error inesperado al actualizar el pago' : 'Ha ocurrido un error inesperado al añadir el pago');
      });
  }

  deleteDeposit(deposit: InvestmentPropertyDepositsModel): void {
    if (this.saving) return;
    this.confirm.open({
      title: 'Eliminar pago',
      subtitle: this.subtitle || undefined,
      message: 'Se eliminará el pago y su asiento contable. Si estaba conciliado, el movimiento bancario volverá a quedar pendiente. Esta acción no se puede deshacer.',
      tone: 'danger',
      details: this.depositDetails(deposit),
      confirmText: 'Eliminar',
      confirmIcon: 'delete',
      loadingText: 'Eliminando pago…',
      errorText: 'Ha ocurrido un error al eliminar el pago',
      action: () => this.investmentPropertyService.depositDelete(deposit.uid).then(ok => { if (ok !== true) throw new Error(); }),
    }).subscribe(deleted => {
      if (!deleted) return;
      this.changed = true;
      this.notify.ok('Pago eliminado correctamente');
      if (this.editing && this.editing.uid === deposit.uid) this.cancelEdit();
      this.loadDeposits(false);
    });
  }

  close(): void {
    if (this.saving) return;
    this.dialogRef.close(this.changed);
  }

  trackByUid(_: number, deposit: InvestmentPropertyDepositsModel): string {
    return deposit.uid;
  }

  depositMeta(deposit: InvestmentPropertyDepositsModel): string {
    return [
      deposit.typePay || 'Sin forma de pago',
      deposit.bankAccountInfo || '',
      Number(deposit.commissions) ? 'Comisión ' + Utils.FormatMoney(deposit.commissions) + ' €' : '',
    ].filter(Boolean).join(' · ');
  }

  private loadDeposits(initial: boolean): void {
    if (initial) {
      this.ready = false;
      this.loadError = false;
    }

    this.investmentPropertyService.depositList(this.data.investment.uid)
      .then(list => {
        this.deposits = Array.isArray(list) ? list : [];
        if (initial) this.resetForm();
        this.openRequestedEdit();
      })
      .catch(() => {
        if (initial) {
          this.loadError = true;
        } else {
          this.notify.error('No se pudieron recargar los pagos');
        }
      })
      .then(() => { this.ready = true; });
  }

  private openRequestedEdit(): void {
    if (!this.data.editUid || this.requestedEditDone) return;
    this.requestedEditDone = true;
    const deposit = this.deposits.find(item => item.uid === this.data.editUid);
    if (deposit) this.startEdit(deposit);
  }

  private lookupKind(typePay: string | null): RealStatePayLookupKind | null {
    if (typePay === this.methods.transfer || typePay === this.methods.debit) return 'bankAccounts';
    switch (typePay) {
      case this.methods.creditCard: return 'paymentCards';
      case this.methods.cash: return 'cashRegisters';
      case this.methods.partner: return 'partners';
      case this.methods.creditPolicy: return 'creditPolicies';
      default: return null;
    }
  }

  private ensureOptions(kind: RealStatePayLookupKind): void {
    if (this.optionRequests.has(kind)) return;
    this.loadingOptions[kind] = true;
    this.configs[kind] = { ...this.configs[kind], hint: 'Cargando…' };
    const request = this.lookups.payOptions(kind)
      .then(list => {
        this.options[kind] = list || [];
        this.loadedKinds.add(kind);
        this.configs[kind] = {
          ...this.configs[kind],
          options: this.options[kind],
          hint: this.options[kind].length ? undefined : this.emptyHints[kind],
        };
        this.applyPendingSelections();
      })
      .catch(() => {
        this.optionRequests.delete(kind);
        this.configs[kind] = { ...this.configs[kind], hint: undefined };
        this.notify.error('No se pudieron cargar las opciones de la forma de pago');
      })
      .then(() => { this.loadingOptions[kind] = false; });
    this.optionRequests.set(kind, request);
  }

  private rememberSelection(kind: RealStatePayLookupKind, id: any, label: string): void {
    if (id === null || id === undefined || id === '') return;
    this.pendingSelection.set(kind, { id, label: label || '' });
  }

  private applyPendingSelections(): void {
    this.pendingSelection.forEach((pending, kind) => {
      if (!this.loadedKinds.has(kind)) return;
      const option = this.findOption(this.options[kind], pending.id) || this.keepMissingOption(kind, pending);
      this.control(kind).setValue(option, { emitEvent: false });
      this.pendingSelection.delete(kind);
    });
  }

  private keepMissingOption(kind: RealStatePayLookupKind, pending: { id: any; label: string }): any {
    const text = String(pending.label || '').trim() || this.missingLabels[kind];
    const option = { id: pending.id, display: text + ' (no disponible)', disabled: true };
    this.options[kind] = [...this.options[kind], option];
    this.configs[kind] = { ...this.configs[kind], options: this.options[kind] };
    return option;
  }

  private findOption(options: { id: any }[], id: any): any {
    const key = String(id);
    return options.find(option => String(option.id) === key)
      || options.find(option => String(option.id).toLowerCase() === key.toLowerCase())
      || null;
  }

  private buildPayload(): InvestmentPropertyDepositsModel {
    const type = this.typePay;
    const payload = new InvestmentPropertyDepositsModel();
    payload.uid = null as any;
    payload.investmentPropertyId = this.data.investment.uid;
    payload.typePay = type as string;
    payload.depositDate = formatDate(this.depositDateControl.value as Date, 'yyyy-MM-dd', 'es-ES') as any;
    payload.amount = this.round(Number(this.amountControl.value));
    payload.commissions = type === this.methods.transfer ? this.round(Number(this.commissionsControl.value) || 0) : 0;
    payload.bankAccountsUid = this.needsBankAccount && this.selectedId('bankAccounts') ? this.selectedId('bankAccounts') : consts.EMPTY_GUID;
    payload.paymentCardsUid = type === this.methods.creditCard && this.selectedId('paymentCards') ? this.selectedId('paymentCards') : consts.EMPTY_GUID;
    payload.efectivoId = type === this.methods.cash ? this.selectedId('cashRegisters') : null as any;
    payload.partnersId = type === this.methods.partner ? Number(this.selectedId('partners')) || 0 : 0;
    payload.polizaCreditoId = type === this.methods.creditPolicy ? this.selectedId('creditPolicies') : null as any;
    return payload;
  }

  private resetForm(): void {
    this.submitted = false;
    this.saveError = '';
    this.pendingSelection.clear();
    this.form.reset({
      typePay: null,
      depositDate: new Date(),
      amount: this.remaining > 0 ? this.remaining : null,
      commissions: null,
      bankAccount: null,
      paymentCard: null,
      cashRegister: null,
      partner: null,
      creditPolicy: null,
    }, { emitEvent: false });
  }

  private depositDetails(deposit: InvestmentPropertyDepositsModel): ConfirmDialogDetail[] {
    const details: ConfirmDialogDetail[] = [
      { label: 'Importe', value: Utils.FormatMoney(Number(deposit.amount) || 0) + ' €' },
    ];
    if (deposit.depositDate) details.push({ label: 'Fecha', value: formatDate(deposit.depositDate, 'dd/MM/yyyy', 'es-ES') });
    if (deposit.typePay) details.push({ label: 'Forma de pago', value: deposit.typePay + (deposit.bankAccountInfo ? ' · ' + deposit.bankAccountInfo : '') });
    return details;
  }

  private guidOrNull(value: string): string | null {
    return value && value !== consts.EMPTY_GUID ? value : null;
  }

  private hasValue(value: any): boolean {
    return value !== null && value !== undefined && String(value).trim() !== '';
  }

  private round(value: number): number {
    return Math.round((Number(value) || 0) * 100) / 100;
  }
}
